import {
  FirebaseAuthentication,
  SignInResult,
} from '@capacitor-firebase/authentication';
import { FirebaseApp } from 'firebase/app';
import {
  AuthCredential,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInAnonymously,
  signInWithCredential,
  User,
  UserCredential,
} from 'firebase/auth';

import _ from 'lodash';
import { IObservableValue, observable, runInAction } from 'mobx';
import { AuthUser } from '../../models/User';
import { IAuthService } from './AuthService';

import { getAuth } from '../../util';
import { Device } from 'util/device';
import { logger } from 'services/Logger';

class FirebaseAuth implements IAuthService {
  app: FirebaseApp;
  user: IObservableValue<AuthUser | null> = observable.box(null, {
    name: 'auth.user',
  });
  userToken: IObservableValue<string | undefined> = observable.box(undefined, {
    name: 'auth.userToken',
  });
  _rawUser?: UserCredential;
  private _isNewUser = false;

  private unsub: Array<() => void> = [];

  constructor(app: FirebaseApp) {
    this.app = app;
    const auth = getAuth(this.app);
    this.unsub.push(
      auth.onAuthStateChanged((user) => {
        // TODO show splash screen until first `authStateChanged` Event!
        logger.debug('authStateChanged', user);
        this.setUser(user, this._isNewUser);
        // runInAction(() => this.user.set(user));
      })
    );
    this.unsub.push(
      auth.onIdTokenChanged(async (user) => {
        const newToken = await user?.getIdToken();
        runInAction(() => this.userToken.set(newToken));
      })
    );
  }

  destroy() {
    this.unsub.forEach((func) => func());
  }

  get isNewUser() {
    return this._isNewUser && this.user.get() !== null;
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    if (this.user.get()) return this.user.get();
    const val = getAuth(this.app).currentUser;

    // this._isNewUser = false;
    runInAction(() => this.user.set(val));

    return val;
  }

  protected setUser(result: User | null, isNewUser: boolean) {
    this._isNewUser = isNewUser;
    runInAction(() => {
      this.user.set(result);
    });
  }

  async signIn(
    result: SignInResult,
    credentials: AuthCredential
  ): Promise<User> {
    let auth = getAuth(this.app);
    logger.debug('signInWithCredential', result, credentials);
    const signInResult = await signInWithCredential(auth, credentials);
    const usr: User = _.defaults({}, result.user, signInResult.user);
    logger.debug('signInWithCredential', usr);

    const isNewUser =
      result.additionalUserInfo?.isNewUser ??
      (signInResult as any)._tokenResponse?.isNewUser ??
      false;

    this.setUser(usr, isNewUser);
    return usr;
  }

  async signInWithGoogle(): Promise<User> {
    const result = await FirebaseAuthentication.signInWithGoogle({
      skipNativeAuth: Device.platform === 'ios',
    });
    const credential = GoogleAuthProvider.credential(
      result.credential?.idToken
    );
    return this.signIn(result, credential);
  }
  async signInWithApple(): Promise<User> {
    const result: SignInResult = await FirebaseAuthentication.signInWithApple({
      skipNativeAuth: true,
    });
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: result.credential?.idToken,
      rawNonce: result.credential?.nonce,
    });
    return this.signIn(result, credential);
  }
  async signInWithMail(email: string, password: string): Promise<User> {
    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email,
      password,
      skipNativeAuth: false,
    });
    if (result.user === null) throw new Error('Login failed');

    const credential = EmailAuthProvider.credential(email, password);
    return this.signIn(result, credential);
  }
  async signUpWithMail(email: string, password: string): Promise<User> {
    const auth = getAuth(this.app);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const usr = _.assign({}, result.user);
    this.setUser(usr, true);
    return usr;
  }

  async signInAnonymously(): Promise<User> {
    const auth = getAuth(this.app);
    const result = await signInAnonymously(auth);
    const usr = result.user;
    this.setUser(usr, false);
    return usr;
  }

  async signOut(): Promise<void> {
    this._isNewUser = false;
    try {
      runInAction(() => this.user.set(null));
      await getAuth(this.app).signOut();
      await FirebaseAuthentication.signOut();
    } catch (e) {
      logger.info('Error loggingout ', e);
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    await FirebaseAuthentication.sendPasswordResetEmail({ email });
  }

  async sendEmailVerification(): Promise<void> {
    await FirebaseAuthentication.sendEmailVerification();
  }
}

export default FirebaseAuth;
